import { useId } from 'react';
import { useIntl } from 'react-intl';
import Button from '@/Components/Marketing/Button';
import { demoHref, livePassportUrl } from '@/Components/Marketing/links';
import Section from '@/Components/Marketing/Section';
import { Cloud } from '@/Components/Marketing/Texture';
import { Heading, Lead } from '@/Components/Marketing/Typography';

/** The printed code (a static file, no QR library in Home's chunks): it opens the example passport. */
export const QR_IMAGE = Object.freeze({ src: '/images/passport-qr.svg', width: 168, height: 168 });

/**
 * Where the closing cloud may show (Texture's Cloud): the code's side, from
 * 768px, stopping short of the text column (it starts past 38% of the
 * section at 768px), so nothing of it reaches the buttons' rings. On a
 * phone, where the buttons run the full width, there is none.
 */
const CLOUD_ZONE = 'inset-y-0 left-0 hidden w-[22%] md:block [mask-image:linear-gradient(to_left,transparent,#000_45%)]';

/**
 * Home's ending: the example passport, as a customer reaches it. From 768px
 * the code to scan leads, on a white card like a printed label, with the
 * line and "See a passport" beside "Book a demo" next to it; on a
 * phone, where scanning the phone's own screen is no use, the line and the
 * buttons alone. The heading stays first in reading order. From 768px the
 * hero's orange cloud comes back once behind the code, turned over, so the
 * page ends as warm as it opens (2026-10-13).
 */
export default function PassportScan() {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });
    const headingId = useId();

    return (
        <Section tone="light" aria-labelledby={headingId} className="relative isolate overflow-hidden border-t border-mk-line">
            <Cloud zone={`-z-10 ${CLOUD_ZONE}`} className="left-[-20rem] top-[-4rem] w-[44rem] rotate-180" />
            <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-[auto_minmax(0,1fr)] lg:gap-20">
                <div>
                    <Heading id={headingId} className="max-w-[14em]">{t('site.home.scan.title')}</Heading>
                    <Lead className="mt-6 max-w-[44ch]">{t('site.home.scan.text')}</Lead>
                    <div className="mt-10 flex flex-wrap gap-3">
                        <Button href={livePassportUrl()} size="lg">
                            {t('site.cta.livePassport')}
                        </Button>
                        <Button href={demoHref()} variant="secondary" size="lg">
                            {t('site.cta.demo')}
                        </Button>
                    </div>
                </div>

                <figure data-passport-code="" className="hidden justify-items-center gap-4 md:order-first md:grid">
                    <div className="rounded-mk-card bg-mk-surface p-5 shadow-mk-soft ring-1 ring-mk-line">
                        <img
                            src={QR_IMAGE.src}
                            width={QR_IMAGE.width}
                            height={QR_IMAGE.height}
                            alt={t('site.platform.customers.qrAlt')}
                            loading="lazy"
                            decoding="async"
                            className="block size-[168px]"
                        />
                    </div>
                    <figcaption className="text-sm text-mk-body">{t('site.platform.customers.scan')}</figcaption>
                </figure>
            </div>
        </Section>
    );
}
